import { useState } from "react";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import CandidateOnboardingCard from "../CandidateOnboardingCard";
import { OnflyLoading } from "../../../../tools/OnflyLoading";
import { ListPagination } from "./ListPagination";
import type { Candidate } from "../../../../types/onboarding";
import type { WhitelistEntry } from "../../../../types/whitelist";

export interface SectionGroup {
  key: string;
  candidates: Candidate[];
  dot: string;
  label: string;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  isLoading?: boolean;
  hasFetched?: boolean;
  /** Elemento extra renderizado no header da seção (ex: chip de filtro). */
  headerExtra?: ReactNode;
}

export interface CandidateSectionAccordionProps {
  sections: SectionGroup[];
  defaultOpenKey?: string | string[];
  /** Chamado quando uma seção é expandida pela 1ª vez — usado pra só buscar
   * os dados dela nesse momento, ao invés de todas as seções de uma vez. */
  onExpand?: (key: string) => void;
  selectedCandidateIds: Set<string | number>;
  onViewDetail: (candidate: Candidate) => void;
  onCancel: (candidate: Candidate) => void;
  onReactivate: (candidate: Candidate) => void;
  onToggleSelect: (candidateId: string | number) => void;
  showHrProgress?: boolean;
  whitelistMap?: Record<string, WhitelistEntry>;
}

export const CandidateSectionAccordion = ({
  sections,
  defaultOpenKey,
  onExpand,
  selectedCandidateIds,
  onViewDetail,
  onCancel,
  onReactivate,
  onToggleSelect,
  showHrProgress,
  whitelistMap,
}: CandidateSectionAccordionProps) => {
  const [openKeys, setOpenKeys] = useState<string[]>(() =>
    Array.isArray(defaultOpenKey) ? defaultOpenKey : (defaultOpenKey ? [defaultOpenKey] : [])
  );
  const openIndexes = sections
    .map((s, i) => (openKeys.includes(s.key) ? i : -1))
    .filter((i) => i >= 0);

  const handleChange = (index: number | number[]) => {
    const indexes = Array.isArray(index) ? index : [index];
    const keys = indexes.map((i) => sections[i]?.key).filter((k): k is string => !!k);
    setOpenKeys(keys);
    keys.forEach((k) => { if (!openKeys.includes(k)) onExpand?.(k); });
  };

  return (
    <Accordion
      allowMultiple
      index={openIndexes}
      onChange={handleChange}
    >
      {sections.map((section) => {
        const isOpen = openKeys.includes(section.key);
        return (
          <AccordionItem key={section.key} border="none" mb={3}>
            <AccordionButton px={0} _hover={{ bg: "transparent" }}>
              <HStack spacing={2} flex={1} textAlign="left">
                <Box w={2} h={2} borderRadius="full" bg={section.dot} flexShrink={0} />
                <Text fontSize="xs" fontWeight="bold" color="gray.500" letterSpacing="wider" textTransform="uppercase">
                  {section.label}
                </Text>
                {section.headerExtra}
              </HStack>
              <AccordionIcon color="gray.400" />
            </AccordionButton>

            {/* Só monta o conteúdo (e o loading) da seção fechada — nunca busca
              * nem renderiza nada antes do usuário abrir o accordion dela. */}
            {isOpen && (
              <AccordionPanel px={0} pb={0} pt={2}>
                {section.isLoading || !(section.hasFetched ?? true) ? (
                  <Flex justify="center" py={6}><OnflyLoading containerSize={40} /></Flex>
                ) : section.candidates.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py={4} fontSize="sm">
                    Nenhum candidato nesta seção.
                  </Text>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {section.candidates.map((c) => (
                      <CandidateOnboardingCard
                        key={c.user_id}
                        candidate={c}
                        onViewDetail={onViewDetail}
                        onCancel={onCancel}
                        onReactivate={onReactivate}
                        isSelected={selectedCandidateIds.has(c.user_id)}
                        onToggleSelect={onToggleSelect}
                        showHrProgress={showHrProgress}
                        whitelistEntry={whitelistMap?.[c.email]}
                      />
                    ))}
                  </VStack>
                )}
                {section.onPageChange && (
                  <ListPagination
                    page={section.page ?? 1}
                    totalPages={section.totalPages ?? 1}
                    onPageChange={section.onPageChange}
                    pageSize={section.pageSize}
                    pageSizeOptions={section.pageSizeOptions}
                    onPageSizeChange={section.onPageSizeChange}
                  />
                )}
              </AccordionPanel>
            )}
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
